/**
 * Global game clock built on top of an AudioContext. Provides high resolution
 * timing utilities aligned to the audio timeline.
 */
import { speedMultiplier, applyOD } from './mods.js';

// --- Global configuration ---------------------------------------------------

/** Central runtime configuration. */
const config = {
  audioOffsetMs: 0,
  inputOffsetMs: 0,
  makerOffsetMs: 0,
};

// Load persisted offsets if available
if (typeof localStorage !== 'undefined') {
  const storedAudio = Number(localStorage.getItem('audio.offsetMs') ?? '0');
  if (!Number.isNaN(storedAudio)) config.audioOffsetMs = storedAudio;
  const storedInput = Number(localStorage.getItem('input.offsetMs') ?? '0');
  if (!Number.isNaN(storedInput)) config.inputOffsetMs = storedInput;
  const storedMaker = Number(localStorage.getItem('maker.offsetMs') ?? '0');
  if (!Number.isNaN(storedMaker)) config.makerOffsetMs = storedMaker;
}

/** Update and persist the global audio offset in milliseconds. */
export function setAudioOffsetMs(value) {
  config.audioOffsetMs = value;
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem('audio.offsetMs', String(value));
  }
}

/** Retrieve the currently configured audio offset in milliseconds. */
export function getAudioOffsetMs() {
  return config.audioOffsetMs;
}

/** Update and persist the global input offset in milliseconds. */
export function setInputOffsetMs(value) {
  config.inputOffsetMs = value;
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem('input.offsetMs', String(value));
  }
}

/** Retrieve the currently configured input offset in milliseconds. */
export function getInputOffsetMs() {
  return config.inputOffsetMs;
}

class Clock {
  /** @type {AudioContext|undefined} */
  ctx;
  /** @type {AudioWorkletNode|undefined} */
  node;
  /** @type {number|undefined} */
  heartbeatId;
  /** @type {boolean} */
  warnedNoWorklet = false;
  /** @type {number} */
  startCtx = 0; // seconds on AudioContext timeline
  /** @type {number} */
  startPerf = 0; // performance.now timestamp (ms) when start() called
  /** @type {number[]} */
  timers = [];
  /** @type {{time: number, cb: Function}[]} */
  events = [];
  /** @type {number} */
  serverOffset = 0; // ms

  /** @type {Promise<void>} */
  ready;

  constructor() {
    if (typeof AudioContext !== 'undefined') {
      this.ctx = new AudioContext();
      this.ready = this.#initWorklet();
    } else {
      this.ready = Promise.resolve();
    }
    this.start();
  }

  /** Underlying AudioContext, if available. */
  get context() {
    return this.ctx;
  }

  /** Mark the start of the timeline. */
  start(startTime) {
    const now = performance.now();
    console.debug('Clock.start', {
      startTime,
      ctxCurrentTime: this.ctx?.currentTime,
      perfNow: now,
    });
    if (this.ctx && startTime !== undefined) {
      this.startCtx = startTime;
      this.startPerf = now + (startTime - this.ctx.currentTime) * 1000;
    } else {
      this.startCtx = this.ctx ? this.ctx.currentTime : 0;
      this.startPerf = now;
    }
  }

  /** Raw current time in milliseconds since {@link start} without user offset. */
  nowRaw() {
    const base = this.ctx
      ? (this.ctx.currentTime - this.startCtx) * 1000
      : performance.now() - this.startPerf;
    return base * speedMultiplier();
  }

  /**
   * Current time adjusted by the global audio offset.
   * @returns {number}
   */
  now() {
    return this.nowRaw() - config.audioOffsetMs;
  }

  /**
  * Schedule a callback to run at the specified time (seconds) on the
  * timeline.
  * @param {number} time
  * @param {Function} cb
  */
  schedule(time, cb) {
    if (this.node) {
      const evt = { time, cb };
      const idx = this.events.findIndex((e) => e.time > time);
      if (idx === -1) this.events.push(evt);
      else this.events.splice(idx, 0, evt);
    } else {
      if (!this.warnedNoWorklet) {
        console.warn('Clock worklet unavailable; using timer-based scheduling');
        this.warnedNoWorklet = true;
      }
      const evt = { time, cb };
      const idx = this.events.findIndex((e) => e.time > time);
      if (idx === -1) this.events.push(evt);
      else this.events.splice(idx, 0, evt);
      this.#ensureFallbackHeartbeat();
    }
  }

  /** Cancel all pending callbacks. */
  clear() {
    for (const id of this.timers) clearTimeout(id);
    this.timers = [];
    this.events = [];
    if (this.heartbeatId !== undefined) {
      const cancel =
        typeof cancelAnimationFrame !== 'undefined' ? cancelAnimationFrame : clearTimeout;
      cancel(this.heartbeatId);
      this.heartbeatId = undefined;
    }
  }

  /** High resolution timestamp (ms) when {@link start} was last called. */
  get startTimestamp() {
    return this.startPerf;
  }

  /** Update internal offset using server-provided time in ms. */
  syncServerTime(serverTime) {
    this.serverOffset = serverTime - performance.now();
  }

  /** Retrieve the estimated current server time in milliseconds. */
  getServerTime() {
    return performance.now() + this.serverOffset;
  }

  async #initWorklet() {
    try {
      // Dynamically import worklet module URL so Node tests do not attempt to load it
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const { default: url } = await import('./audio/clock.worklet?url');
      await this.ctx.audioWorklet.addModule(url);
      this.node = new AudioWorkletNode(this.ctx, 'clock-worklet');
      this.node.connect(this.ctx.destination);
      this.node.port.onmessage = (e) => {
        const now = (e.data.time - this.startCtx) * speedMultiplier();
        while (this.events.length && this.events[0].time <= now) {
          const ev = this.events.shift();
          ev && ev.cb();
        }
      };
    } catch (err) {
      console.warn('Failed to load clock worklet, falling back to timers', err);
      // Ignore worklet failures and fall back to timer-based scheduling
    }
  }

  #ensureFallbackHeartbeat() {
    if (this.heartbeatId !== undefined) return;
    const raf =
      typeof requestAnimationFrame !== 'undefined'
        ? requestAnimationFrame
        : (fn) => setTimeout(() => fn(performance.now()), 16);
    const tick = () => {
      const speed = speedMultiplier();
      const now = this.ctx
        ? (this.ctx.currentTime - this.startCtx) * speed
        : ((performance.now() - this.startPerf) / 1000) * speed;
      while (this.events.length && this.events[0].time <= now) {
        const ev = this.events.shift();
        ev && ev.cb();
      }
      this.heartbeatId = raf(tick);
    };
    this.heartbeatId = raf(tick);
  }
}

export const GameClock = new Clock();

// --- Beat event hooks ------------------------------------------------------
/** @typedef {(beat: number) => void} BeatListener */
const beatListeners = [];

export function emitBeat(beat) {
  for (const listener of beatListeners) listener(beat);
}

// --- Judgement windows -----------------------------------------------------

/** Overall difficulty value used to calculate judgement windows. */
const DEFAULT_OD = 8;
let baseOD = DEFAULT_OD;

/**
 * Update the base OD used for judgement windows.
 * @param {number} value
 */
export function setOD(value) {
  baseOD = value;
}

function currentOd() {
  return applyOD(baseOD);
}

/** Timing window in milliseconds for a 300 judgement. */
function window300() {
  return 64 - 3 * currentOd();
}
/** Timing window in milliseconds for a 100 judgement. */
function window100() {
  return 97 - 3 * currentOd();
}
/** Timing window in milliseconds for a 50 judgement. */
export function window50() {
  return 151 - 3 * currentOd();
}

/** Timing window in milliseconds for slider ticks and repeats. */
export function sliderWindow() {
  return 50 / speedMultiplier();
}

/**
 * Determine the judgement for a note hit at the given offset.
 * @param {number} offsetMs The timing offset in milliseconds.
 * @returns {300|100|50|'Miss'}
 */
export function judge(offsetMs) {
  const diff = Math.abs(offsetMs);
  if (diff <= window300()) return 300;
  if (diff <= window100()) return 100;
  if (diff <= window50()) return 50;
  return 'Miss';
}
